<template>
  <div class="shop" :class="{detailOption: detailOption, hiddenDefaultThumb: getShopSettings.hiddenDefaultThumb}">
    <OrderTimer
      v-if="shopOrderTimer"
      :locale="locale"
      :isStore="isStore"
      :responsive="responsive"
      :key="shopPageId"
      :shopPageId="shopPageId" />
    <WaitingOrder
      v-if="shopWaitingOrder"
      :shopPageId="shopPageId" />
    <Category
      v-if="category && ready"
      class="shopCategory"
      :locale="locale"
      :lang="lang"
      :responsive="responsive"
      :shopPageId="shopPageId"
      :category="category"
      :isDetail="detail"
      :isCart="viewCart" />
    <div
      v-if="itemGroup && ready"
      class="shopItemList">
      <ItemGroup
        v-for="(group, index) in itemGroupList"
        :key="index"
        :locale="locale"
        :lang="lang"
        :shopPageId="shopPageId"
        :group="group"
        :isAuth="isAuth"
        @addCart="addCart"
        @setToastMessage="setToastMessage" />
    </div>
    <ShopNav
      class="shopNav"
      :locale="locale"
      :lang="lang"
      :shopPageId="shopPageId"
      :roomKey="roomKey"
      :isPublic="isPublic"
      :isAuth="isAuth"
      @showMypage="showMypage"
      @showCart="viewCart = true" />
    <Detail
      v-if="detail"
      ref="itemDetail"
      :locale="locale"
      :lang="lang"
      :isAuth="isAuth"
      :responsive="responsive"
      :shopPageId="shopPageId"
      :shopResponsive="shopResponsive"
      :storeItemOption="detailOption"
      @addCart="addCart"
      @update="updateItemProperty"
      @setDetailOption="setDetailOption" />
    <CallStaff
      v-if="callStaff"
      :locale="locale"
      :lang="lang"
      :isAuth="isAuth"
      :responsive="responsive"
      :shopPageId="shopPageId"
      :shopResponsive="shopResponsive"
      @setToastMessage="setToastMessage"
      @complete="setToastMessage" />
    <GuestCount
      v-if="isGuestCount"
      :locale="locale"
      :lang="lang"
      :isPublic="isPublic"
      :responsive="responsive"
      :shopPageId="shopPageId"
      :guestCountError="guestCountError"
      @close="setGuestCount(false)" />
    <StoreMypage v-if="viewMypage && isStore"
      :locale="locale"
      :lang="lang"
      :responsive="responsive"
      :shopPageId="shopPageId"
      @setToastMessage="setToastMessage"
      @close="closeMypage" />
    <StoreCart v-if="viewCart && !detail && isStore && !timeIsExpired"
      :locale="locale"
      :lang="lang"
      :isPublic="isPublic"
      :responsive="responsive"
      :shopPageId="shopPageId"
      :visible="viewCart"
      @setToastMessage="setToastMessage"
      @noCartItems="noCartItems"
      @setDetailOption="setDetailOption"
      @complete="completeSubmit"
      @close="closeViewCart" />
    <div v-if="viewCart && !detail && !isStore && !timeIsExpired" id="floatingCart">
      <FloatingCart
        :locale="locale"
        :lang="lang"
        :isPublic="isPublic"
        :responsive="responsive"
        @setToastMessage="setToastMessage"
        @complete="completeSubmit"
        @close="closeViewCart" />
    </div>
    <ToastMessage v-if="toastMessage"
      :locale="locale"
      :message="toastMessage"
      :dur="toastDuration"
      @close="setToastMessage(null)" />
  </div>
</template>

<script>
import '@/assets/css/shop.scss'
import { startTimeTicker, stopTimeTicker } from '@/components/Shop/timeTicker' // 아이템 이용시간 체크용 타이머
import UNIQUE from '@/common/unique'
import Category from '@/components/Shop/Category'
import ItemGroup from '@/components/Shop/ItemGroup'
import Detail from '@/components/Shop/Detail'
import ShopNav from '@/components/Shop/Nav'
import FloatingCart from '@/views/Cart'
import StoreCart from '@/components/Shop/Cart'
import StoreMypage from '@/components/Shop/MyOrder'
import ToastMessage from '@/components/Util/ToastMessage'
import DetailOptions from '../components/Shop/DetailOptions.vue'
import CallStaff from '@/components/Shop/CallStaff'
import OrderTimer from '@/components/Shop/OrderTimer'
import WaitingOrder from '@/components/Shop/WaitingOrder'
import GuestCount from '@/components/Shop/GuestCount'

export default {
  name: 'shop',
  props: [
    'locale',
    'lang',
    'responsive',
    'shopResponsive',
    'roomKey',
    'isPublic',
    'shopPageId',
    'shopCategoryId'
  ],
  components: {
    Category,
    ItemGroup,
    Detail,
    ShopNav,
    FloatingCart,
    ToastMessage,
    DetailOptions,
    StoreCart,
    StoreMypage,
    CallStaff,
    OrderTimer,
    WaitingOrder,
    GuestCount
  },
  computed: {
    getGuestInfo () {
      const authInfo = this.$store.getters['authentication/getAuthInfo'] //파라미터 정보
      const guestInfo = this.$store.getters['guest/getGuestUserInfo']
      return {
        ...authInfo,
        ...guestInfo
      }
    },
    isStore () {
      return (this.$route.params && this.$route.params.shopType === 'store') || this.$route.name === 'store'
    },
    cartList () {
      return this.$store.getters['cart/getList'](this.shopPageId)
    },
    getShopResultData () {
      return this.$store.getters['shop/getShopResultData']
    },
    detail () {
      const detailItem = this.$store.getters['shop/getDetailItem']
      return detailItem && detailItem.shopItemId
    },
    getHiddenItems () {
      return this.$store.getters['shop/getHiddenItems']
    },
    itemGroupList () {
      const itemGroup = this.itemGroup || []
      const hiddenItems = this.getHiddenItems
      const hiddenSet = new Set(hiddenItems)

      return itemGroup.map(group => {
        const items = Array.isArray(group.items) ? group.items : []
        const hasItems = items.length > 0
        const allHidden = hasItems && items.every(item => item && hiddenSet.has(item.shopItemId))

        return {
          ...group,
          isHiddenGroup: !hasItems || allHidden
        }
      })
    },
    callStaff () {
      return this.$store.getters['shop/getCallStaff'].isActive
    },
    timeIsExpired () {
      return this.$store.getters['shop/getShopOrderTimeout'](this.shopPageId)
    },
    getShopSettings () {
      return this.$store.getters['shop/getShopSettings'](this.shopPageId)
    }
  },
  beforeDestroy () {
    this.$store.commit('shop/setShopGuest', null)
    this.$store.commit('shop/setBillingInfo', null)

    stopTimeTicker()

    // 카테고리 안정화 타이머 정리
    clearTimeout(this._catStableTimer)

    // 페이지 데이터 로딩 인터벌 정리
    if (this._pageDataInterval) {
      clearInterval(this._pageDataInterval)
      this._pageDataInterval = null
    }

    // 최초 인터벌 세팅용 타임아웃 정리
    if (this._pageDataTimeout) {
      clearTimeout(this._pageDataTimeout)
      this._pageDataTimeout = null
    }
  },
  watch: {
    async '$route' (to, from, next) {
      if ((to.name === from.name) && (to.params.shopPageId !== from.params.shopPageId)) {
        this.init()
      }
    },
    isStore (isStore) {
    },
    shopCategoryId: {
      handler(val) {
        clearTimeout(this._catStableTimer)
        const captured = val
        this._catStableTimer = setTimeout(async () => {
          // 그동안 값이 바뀌지 않았으면 확정
          if (this.shopCategoryId === captured) {
            await this.getShopPageProperty()
            await this.getItemGroups()
            await this.setDisplayPresets()
            await this.getBillingInfo()
            this.checkAuth()
          }
        }, 1000)
      }
    },
    detail (isDetail) {
      const isLayerOn = isDetail ? 'add' : 'remove'
      document.body.classList[isLayerOn]('layerOn')
      if (isDetail) {
        this.setToastMessage(null)
      }
    },
    async callStaff (isOpen) {
      await this.getShopPageProperty()
      const isLayerOn = isOpen ? 'add' : 'remove'
      document.body.classList[isLayerOn]('layerOn')
    }
  },
  created () {
    this.init(true)
    startTimeTicker()
  },
  mounted () {
    this._pageDataTimeout = setTimeout(() => {
      this.dataLoadInterval()
    }, this.intervalDur)
  },
  data () {
    return {
      intervalDur: 60000,
      _pageDataInterval: null,
      _catStableTimer: null,
      _pageDataTimeout: null,
      ready: false,
      isAuth: false,
      category: null,
      list: null,
      itemGroup: null,
      viewMypage: false,
      viewCart: false,
      toastMessage: null,
      toastDuration: 2000,
      detailOption: null,
      shopOrderTimer: null,
      shopWaitingOrder: null,
      isGuestCount: false,
      guestCountError: null
    }
  },
  methods: {
    setGuestCount (isOpen) {
      // if (!this.guestCountError) return
      this.isGuestCount = isOpen
    },
    async dataLoadInterval () {
      // 1분마다 페이지 프로퍼티 + 빌링인포 호출
      this._pageDataInterval = setInterval(async () => {
        // 인원선택 레이어가 떠있는 동안에는 실제 호출X
        if (this.isGuestCount) return
        this.init()
      }, this.intervalDur)
    },
    async init (isInit) {
      if (isInit) {
        this.ready = false
        this.category = null
        this.list = null
        this.itemGroup = null
        this.viewCart = false
        this.guestCountError = null
      }
      const guestInfo = this.getGuestInfo
      const hasGuestInfo = guestInfo && guestInfo.space && (guestInfo.k && guestInfo.otavalid !== 'false')
      if (isInit) this.$store.commit('loading', true)
      // 샵페이지 구성에 필요한 프로퍼티 (카테고리, 카테고리별 item인덱스, item프로퍼티, ...)
      const getShopPageProperty = await this.getShopPageProperty()
      if (!getShopPageProperty) return
      /** 
       * 인원수 입력이 필요한 업장은
       * 우선 인원입력부터
       * 인원수 입력은 반드시 게스트정보(space+ota)가 필요하므로
       * 없으면 isAuth = false인 상태로 스토어 진입(주문불가)
       */
      if (
        !this.isGuestCount &&
        hasGuestInfo &&
        getShopPageProperty.pageData &&
        getShopPageProperty.pageData.guestSettings &&
        getShopPageProperty.pageData.guestSettings.active
      ) {
        const shopGuest = (getShopPageProperty.catCode && await this.$store.dispatch('order/getShopGuestInfo', getShopPageProperty.catCode)) || null
        if (shopGuest && shopGuest.preGuestInfo) {
          // 사전 등록된 인원선택 정보 (e.g master tablet)
          this.setShopGuestInfo(shopGuest.preGuestInfo)
        } else if (!(shopGuest && shopGuest.guestInfo)) {
          // 인원선택 레이어
          this.setGuestCount(true)
          this.$store.commit('loading', false)
        }
      }
      await this.getItemGroups() // 프로퍼티를 바탕으로 아이템리스트 그룹핑하여 배열로 정렬
      await this.setDisplayPresets() // 각 아이템에 리스트단에서 필요한 프리셋정보 호출 (이때 호출한 프리셋 정보는 스토어에 저장하고 각 아이템 컴포넌트에서 필요한 프리셋 매핑)
      await this.getBillingInfo()
      if (isInit) {
        this.$store.dispatch('shop/setShopOrderTimeout', { shopPageId: this.shopPageId, isTimeout: false })
        this.checkAuth()
        this.$store.commit('loading', false)
      }
      this.ready = true
    },
    track () {
      const site = this.$store.state.authentication.site
      const routerPath = this.$route.path
      const path = `${site}${routerPath}`
      const title = this.getCurrentCategoryLabel()
      this.$gtag.pageview({
        page_path: path,
        page_title: title || 'shop'
      })
    },
    async setShopGuestInfo (preGuestInfo) {
      const data = {
        pageId: this.shopPageId,
        guestInfos: preGuestInfo
      }
      try {
        const res = await this.$store.dispatch('order/setShopGuestInfo', data)
        if (res && res.status === 200) {
          const guestData = {
            billingStayNo: '',
            guestInfo: {
              guestInfos: data.guestInfos,
              orderUUID: ''
            },
            startTime: new Date().toISOString()
          }
          this.$store.commit('shop/setShopGuest', guestData)
          this.guestCountError = null
        }
        else if (res && res.status === 403) {
          const invalidGroupId = res && res.result && res.result.reason && res.result.reason.includes('groupId')
          const message = invalidGroupId ? 'message_invalid_guest_groupid' : 'system_error'
          this.guestCountError = message
          this.setGuestCount(true)
        }
      } catch {}
    },
    updateItemQuantity () {

    },
    async getBillingInfo () {
      // ota기준 catCode별 주문내역 카운트
      const shopPageId = this.shopPageId
      const shopCatCode = this.$store.getters['shop/getShopCatCode'](shopPageId)
      const billingInfo = await this.$store.dispatch('order/getBillingInfo', shopCatCode)
      const billingData = billingInfo && {
        billingCount: billingInfo.orderCounting,
        billingInfo: {
          startDate: billingInfo.startDate,
          lastOrderDate: billingInfo.lastOrderDate
        }
      }
      if (billingData) {
        this.$store.commit('shop/setBillingInfo', {
          catCode: shopCatCode,
          ...billingData
        })
      }
    },
    getCurrentCategoryLabel () {
      const categoryLabel = getCategoryLabel(this.category, this.shopCategoryId)
      // GA 데이터 수집을 위한 현재 카테고리 이름
      function getCategoryLabel(property, categoryId) {
        // property는 categoryProperty 배열
        for (const item of property) {
          // categoryId가 일치하면 해당 categoryLabel 반환
          if (item.categoryId === categoryId) {
            return item.categoryLabel
          }
          // subCategory가 있다면, 재귀적으로 해당 항목을 검색
          if (item.subCategory) {
            const result = getCategoryLabel(item.subCategory, categoryId)
            if (result) {
              return result
            }
          }
        }
        // 찾지 못한 경우 null 반환
        return null
      }
      return (categoryLabel && categoryLabel[this.lang]) || null
    },
    checkAuth () {
      const getAuthInfo = this.$store.getters['authentication/getAuthInfo']
      const key = getAuthInfo.k
      const space = getAuthInfo.space
      const isKeyValid = !(getAuthInfo.otavalid && getAuthInfo.otavalid === 'false')
      this.isAuth = key && space && isKeyValid
    },
    getCartList () {
      const site = this.getGuestInfo.site
      const ota = this.getGuestInfo.k
      const shopPageId = this.shopPageId
      this.$store.commit('cart/initList')
      if (localStorage.length && ota) {
        for (let i = 0; i < localStorage.length; i++) {
          // cart_pageId로 시작하는 items
          const storage = localStorage.key(i)
          if (storage.includes('cart_') && storage.includes(site)) {
            const cartItem = JSON.parse(localStorage.getItem(localStorage.key(i)))
            let item = Object.assign({}, cartItem)
            item.cartId = storage
            if (item.cartOta && item.cartOta === ota) {
              this.$store.commit('cart/addList', item)
            }
          }
        }
      }
    },
    async getShopPageProperty () {
      function extractShopItems(arr, parentPath = []) {
        let result = []
        for (const category of arr) {
          const {
            categoryId,
            categoryLabel,
            shopItemsIndex,
            subCategory
          } = category
          const currentPath = [
            ...parentPath,
            { categoryId, categoryLabel }
          ]
          if (shopItemsIndex) {
            result.push({
              categoryId,
              categoryLabel,
              shopItemsIndex,
              categoryPath: parentPath.length ? parentPath : null // 최상위 카테고리는 null
            })
          }
          if (subCategory) {
            result = result.concat(extractShopItems(subCategory, currentPath))
          }
        }
        return result
      }
      // this.category = null
      // this.list = null
      try {
        const data = {
          shopPageId: this.shopPageId
        }
        // 최신 버전
        const getLastVersion = await this.$store.dispatch('shop/getLastVersion', data.shopPageId)
        const version = getLastVersion && getLastVersion.message && getLastVersion.message.last_version
        // 인덱스디비에 있는지, 최신 버전인지
        const indexedData = await this.$store.dispatch('versionhistory/getIndexedDb', {
          table: 'shop',
          id: data.shopPageId,
          version: version || 0
        })
        // 버전이 다르거나 인덱스디비에 없으면 샵프로퍼티 호출 아니면 인덱스 데이터 리턴
        const res = (!indexedData || indexedData === 'diffVersion')
          ? await this.$store.dispatch('shop/getOrderPageProperty', data)
          : indexedData
        const property = (!indexedData || indexedData === 'diffVersion')
          ? res && res.property
          : indexedData
        const removeEmptySubCategory = (arr) => { // 서브카테고리가 비어있으면 필요없음
          return arr.map(({ subCategory, ...rest }) => {
            return subCategory && subCategory.length
              ? { ...rest, subCategory: removeEmptySubCategory(subCategory) }
              : rest
          })
        }

        if ((!indexedData || indexedData === 'diffVersion') && res && res.property) {
          this.$store.dispatch('versionhistory/setIndexedDb', {
            table: 'shop',
            id: data.shopPageId,
            version: res.version,
            data: res.property
          })
        }
        if (property) {
          if (property.catCode) {
            this.$store.commit('shop/setCatCodes', { shopPageId: data.shopPageId, catCode: property.catCode })
          }
          if (property.shopItemsProperty) {
            this.$store.dispatch('shop/ingestItemProperty', property.shopItemsProperty)
          }
          if (property.pageData && property.pageData.settings) {
            this.$store.commit('shop/setShopSettings', {
              shopPageId: data.shopPageId,
              settings: {
                hiddenOptionRequirement: false,
                ...property.pageData.settings,
                ...property.pageData.guestSettings && {
                  guestSettings: property.pageData.guestSettings
                },
                ...property.pageData.orderLimitSetting && {
                  orderLimitSetting: property.pageData.orderLimitSetting
                },
                isUseCallStaff: property.pageData.isUseCallStaff === true,
                excludeTax: property.pageData.excludeTax || false
              }
            })
            this.$store.commit('shop/setShopCurrency', property.pageData.currencyCode)
          }
          if (property.pageData && property.pageData.orderLimitSetting) {
            this.shopOrderTimer = property.pageData.orderLimitSetting.orderableTime
            this.shopWaitingOrder = property.pageData.orderLimitSetting.waitingOrder
          }
          const categoryProperty = property.categoryProperty.filter(x => x.categoryId !== 'call-staff')
          const callStaff = property.categoryProperty.find(x => x.categoryId === 'call-staff')

          if (callStaff) {
            this.$store.commit('shop/setCallStaffItems', callStaff.shopItemsIndex)
          }
          this.category = removeEmptySubCategory(categoryProperty)
          this.$store.commit('shop/setCategoryProperty', {
            shopPageId: data.shopPageId,
            category: removeEmptySubCategory(categoryProperty)
          })
          this.track()
          this.list = extractShopItems(categoryProperty) // 렌더링에 필요한 카테고리별 아이템순서
          return property
        } else {
          const status = (res && res.message && res.message.code) || 500
          const message = this.getShopResultData(status)
          this.setToastMessage(message)
          // this.$router.push({name: 'main'})
          return false
        }
      } catch (ex) {
        return false
      }
    },
    async getItemGroups () {
      const getItemProperty = this.$store.getters['shop/getItemProperty']
      const lang = this.lang
      const list = this.list
      const isHiddenAvailableTime = this.isHiddenAvailableTime
      this.itemGroup = list.map(x => {
        const selfName = [x.categoryLabel]
        return {
          categoryId: x.categoryId,
          itemGroupName: x.categoryPath && x.categoryPath.length
            ? x.categoryPath.map(c => c.categoryLabel).concat(selfName)
            : selfName,
          items: x.shopItemsIndex
            .map(shopItemId => getItemProperty(shopItemId, this.isStore))
            .filter(item => item && item.categoryIds && item.categoryIds.includes(x.categoryId)) // 인덱스는 남아있는데 아이템에 카테고리 정보는 빠져있는 경우
        }
      })
      return this.itemGroup
    },
    async setDisplayPresets () {
      function getPresetIds (itemGroup) { // itemGroup은 반드시 ItemGroup.items 구조를 갖는다
        const availableTimes = new Set()
        const promotions = new Set()
        const status = new Set()
        const details = new Set()
        itemGroup.forEach(group => {
          group.items.forEach(item => {
            if (item && item.availableTime) {
              availableTimes.add(item.availableTime)
            }
            if (item && item.displayTime) {
              availableTimes.add(item.displayTime)
            }
            if (item && item.promotion) {
              promotions.add(item.promotion)
            }
            if (item && item.promotions) {
              item.promotions.map(presetId => {
                promotions.add(presetId)
              })
            }
            if (item && item.status) {
              status.add(item.status)
            }
            if (item && item.descList && item.descList.find(x => x && !x.desc && typeof (x) === 'string')) {
              item.descList.filter(x => x && !x.desc && typeof (x) === 'string').forEach(desc => {
                details.add(desc)
              })
            }
            if (item && item.optionProperty && item.optionProperty.length > 0) {
              item.optionProperty.forEach(option => {
                const optionItems = option && option.optionItems

                if (optionItems && optionItems.length > 0) {
                  optionItems.forEach(optionItem => {
                    if (optionItem && optionItem.status) {
                      status.add(optionItem.status)
                    }
                  })
                }
              })
            }
          })
        })
        return [
          ...availableTimes,
          ...promotions,
          ...status,
          ...details
        ]
      }
      const presetIds = getPresetIds(this.itemGroup)
      const presetItemsAll = await this.getStorePresetItemsAll(presetIds)
      if (presetItemsAll) {
        this.$store.commit('shop/setPreset', presetItemsAll)
      }
      return presetItemsAll
    },
    async getStorePresetItemsAll (presetIds) { // 아이템에 들어가 있는 preset ids
      try {
        const res = await this.$store.dispatch('shop/getStorePresetItems', presetIds)
        const presetItem = res && res.message && res.message.presetItem
        return presetItem
      } catch (ex) {
        return false
      }
    },
    updateItemProperty (shopItemId) {
      this.getItemGroups()
    },
    async addCart (data) {
      this.setDetailOption()
      this.setToastMessage(null)
      const cartList = this.cartList
      let messageAddCartItem = ''

      if (this.timeIsExpired) {
        this.setToastMessage(this.locale.alert_order_time_over)
        this.$store.commit('shop/setDetailItem', null)
        return
      }
      if (data) {
        /**
         * 기존 장바구니 상품과 shopItemId가 동일하고
         * 해당 상품이 단품인 경우는 수량을 합쳐서 업데이트 (existItem)
         * 단품이 아닌 옵션 상품인 경우는 수량 자체를 그대로 업데이트 (replaceItem)
         */
        const existItem = cartList && cartList.filter(x => x.cartId && x.cartId.includes('shop')).find(item => {
          const hasOptions = item.payload.options && item.payload.options.length >= 1
          if (item.shopPageId === data.shopPageId && item.shopItemId === data.shopItemId && !hasOptions && !(data.payload.options && data.payload.options.length >= 1)) {
            return item
          }
        })

        const replaceItem = cartList && cartList.filter(x => x.cartId && x.cartId.includes('shop')).find(item => {
          const hasOptions = item.payload.options && item.payload.options.length >= 1
          if (item.cartId === data.cartId && hasOptions) {
            return item
          }
        })

        const cartData = {
          cartId: existItem
            ? existItem.cartId : `cart_shop_${this.getGuestInfo.site}_${UNIQUE.fileid()}_${data.shopPageId}_${data.shopItemId}`,
          cartIdx: existItem ? existItem.cartIdx : cartList.length,
          cartOta: this.getGuestInfo.k
        }
        const cartItem = {
          ...cartData,
          ...data
        }
        const cartItemName = data.payload.shopItemName[this.lang]
        messageAddCartItem = this.locale.message_add_cart_item_new.replace('{var}', `"${cartItemName}"`)

        // 단품은 기존 담은 수량 + 추가한 수량
        if (existItem && existItem.cartId) {
          cartItem.payload.quantity += existItem.payload.quantity
          messageAddCartItem = this.locale.message_add_cart_item.replace('{var}', `"${cartItemName}"`)
        }

        // 옵션 상품은 그전 수량 그대로
        if (replaceItem && replaceItem.cartId) {
          cartItem.payload.quantity = replaceItem.payload.quantity
          this.$store.commit('cart/delList', replaceItem.cartId)
        }

        this.$nextTick(() => {
          localStorage.setItem(cartData.cartId, JSON.stringify(cartItem))
          this.$store.commit('cart/addList', cartItem)
          this.setToastMessage(replaceItem
            ? this.locale.update_option
            : messageAddCartItem)
        })
      }
      if (this.viewCart) {
        this.viewCart = false
      }

      this.$nextTick(async () => {
        this.getCartList()
        /**
         * 수량별 옵션이 필요한 상품이면
         * 최소 주문 수량에 만족할 때까지
         * 상품디테일로 다시 돌아가야 함
         */
        const checkQuantity = this.$store.getters['cart/getCartShopItems'](data.shopItemId, this.isStore)
        const remainOrder = checkQuantity && (checkQuantity.required - checkQuantity.currentQuantity)
        if (remainOrder >= 1) {
          const remainMessage = this.locale.message_shop_remain_order.replace('{var}', remainOrder)
          this.setToastMessage(`${messageAddCartItem}\n${remainMessage}`)
          if (this.$refs.itemDetail) {
            this.$refs.itemDetail.orderAgain()
          }
        } else {
          this.viewCart = true
          this.$store.commit('shop/setDetailItem', null)
        }
      })
    },
    setToastMessage (message, dur) {
      this.toastMessage = null
      this.toastMessage = message
      this.toastDuration = dur || 2000
    },
    completeSubmit (message) {
      this.setToastMessage(message)
      this.getBillingInfo()
      this.closeViewCart()
    },
    setDetailOption (shopItemId) {
      this.detailOption = shopItemId || null
      this.$store.commit('shop/setOpenDetailOption', shopItemId ? true : false)
    },
    async showMypage () {
      if (this.isStore) {
        await this.$store.dispatch('authentication/getOtaKey')
        this.viewMypage = true
        document.body.classList.add('layerOn')
      } else {
        this.$router.push({
          name: 'myOrder'
        })
      }
    },
    closeMypage (e) {
      if (e) {
        e.preventDefault()
      }
      this.viewMypage = false
      document.body.classList.remove('layerOn')
    },
    closeViewCart (e) {
      this.viewCart = false
    },
    noCartItems () {
      this.setToastMessage(this.locale.nodata_cart_item)
      this.closeViewCart()
    }
  }
}
</script>